/** The deployment's configurable values - `GET /api/v1/config`.
 *
 * KD's rule of 2026-11-01: nothing environment-, market- or customer-specific is hardcoded; each
 * such value has one configurable home. For the browser that home is this read. **The SPA holds no
 * copy** of the market defaults (country, jurisdiction, regime, locale, currency, time zone), the
 * support address or the retention policy: the server's `Settings` is the one place they are set.
 *
 * Thrown on failure, never defaulted - `data ?? "GB"` would be a second home wearing a fallback's
 * clothes, and wrong in exactly the case it exists for. Consumers treat `undefined` as "not known
 * yet" and either wait or leave the field for the person to choose.
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type DeploymentConfig = components["schemas"]["ConfigOut"];
export type MarketDefaults = DeploymentConfig["market"];

export const configQuery = {
  queryKey: ["config"] as const,
  queryFn: async (): Promise<DeploymentConfig> => {
    const { data, error, response } = await api.GET("/api/v1/config", {});
    if (error || !data) {
      throw refusalFrom(response, error, "The deployment settings could not be read.");
    }
    return data;
  },
  // Changes when the server is redeployed and at no other time.
  staleTime: 10 * 60_000,
  retry: false,
} as const;

export function useConfig() {
  return useQuery(configQuery);
}

/** The market defaults, or `undefined` until the read lands (or if it failed). */
export function useMarketDefaults(): MarketDefaults | undefined {
  return useConfig().data?.market;
}
